import { APP_CONFIG } from '../constants';

export type ImageType = 'toy' | 'user' | 'badge' | 'post' | 'collection' | 'chat';

const API_BASE = APP_CONFIG.API_URL; // e.g. https://localhost:7169

/**
 * Returns a fully-qualified image URL.
 * - If img is already an absolute URL (http/https) → returned as-is
 * - If img starts with /Image → prepend API_BASE
 * - Otherwise → construct the path based on type
 *
 * Usage: <img src={getImageUrl(photo, brandName, 'toy')} />
 * No need to prefix with API_BASE at the call site.
 */
export const getImageUrl = (
    img?: any | null,
    brandName?: string | null,
    type: ImageType = 'toy'
): string | undefined => {
    // If img is an object (common in some API responses), extract the URL
    let imgPath = img;
    if (img && typeof img === 'object') {
        imgPath = img.mediaUrl || img.MediaUrl || img.Url || img.url || img.imageUrl;
    }

    if (!imgPath || imgPath === 'default.jpg' ||
        (typeof imgPath === 'string' && /(^|\/)userimage\.jpg(?:\?.*)?$/i.test(imgPath))) {
        if (type === 'user') return undefined;
        return APP_CONFIG.DEFAULT_IMAGES.MODEL;
    }

    // Use imgPath for all subsequent checks
    const imgStr = imgPath as string;

    // Already an absolute URL → return as-is (but map host if it's our own image endpoint to ensure dev/prod interop)
    if (typeof imgStr === 'string' && (imgStr.startsWith('http://') || imgStr.startsWith('https://'))) {
        // Do not rewrite global catalog ToyImage assets because they only exist on the production server,
        // unless it's a localhost URL and we are not running locally.
        const isLocalHostImg = imgStr.includes("localhost") || imgStr.includes("127.0.0.1");
        const isLocalApp = typeof window !== "undefined" &&
            (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

        if (imgStr.toLowerCase().includes('collectohub.com') && !(isLocalHostImg && !isLocalApp)) {
            return imgStr;
        }
        const urlPattern = /https?:\/\/[^\/]+\/Image\/(.*)/i;
        const match = imgStr.match(urlPattern);
        if (match) {
            return `${API_BASE}/Image/${match[1]}`;
        }
        return imgStr;
    }

    // Legacy tilde paths → strip tilde, prepend API_BASE
    if (typeof imgStr === 'string' && imgStr.startsWith('~')) {
        return `${API_BASE}/${imgStr.substring(1).replace(/^\/+/, '')}`;
    }

    // Already a full /Image or /uploads path → prepend API_BASE
    if (typeof imgStr === 'string' && (imgStr.startsWith('/Image') || imgStr.startsWith('Image/') || imgStr.startsWith('/uploads') || imgStr.startsWith('uploads/'))) {
        const formattedPath = imgStr.startsWith('/') ? imgStr : `/${imgStr}`;
        // Special case: redirect legacy uploads/messages to Image/ChatImages
        if (formattedPath.startsWith('/uploads/messages/')) {
            return `${API_BASE}/Image/ChatImages/${formattedPath.replace('/uploads/messages/', '')}`;
        }
        return `${API_BASE}${formattedPath}`;
    }

    // Strip leading slash from relative filename to avoid double slash when prepending path
    const sanitizedImg = typeof imgStr === 'string' ? imgStr.replace(/^\/+/, '') : '';

    if (!sanitizedImg) return undefined;

    // Relative filename → construct path based on type
    switch (type) {
        case 'user':
            return `${API_BASE}/Image/userimage/${sanitizedImg}`;
        case 'badge':
            return `${API_BASE}/Image/BadgeImage/${sanitizedImg}`;
        case 'post':
            return `${API_BASE}/Image/post-images/${sanitizedImg}`;
        case 'chat':
            return `${API_BASE}/Image/ChatImages/${sanitizedImg}`;
        case 'collection':
            return `${API_BASE}/Image/CollectionImages/${sanitizedImg}`;
        case 'toy':
        default: {
            // Brand name → folder name (strip spaces). Fallback to HotWheels.
            const brandFolder = typeof brandName === 'string' && brandName.trim() !== ''
                ? brandName.replace(/\s+/g, '')
                : APP_CONFIG.FALLBACKS.BRAND_FOLDER;
            return `${API_BASE}/Image/ToyImage/${brandFolder}/${sanitizedImg}`;
        }
    }
};

/**
 * Convenience helper: returns a user avatar URL from an ImageUrl field value.
 */
export const getUserAvatarUrl = (imageUrl?: string | null): string | undefined =>
    getImageUrl(imageUrl, null, 'user');

const getOptimizableImagePath = (source?: string): string | null => {
    if (!source) return null;
    try {
        const url = new URL(source, typeof window !== 'undefined' ? window.location.origin : APP_CONFIG.API_URL);
        if (!url.pathname.toLowerCase().startsWith('/image/')) return null;
        if (!url.hostname.toLowerCase().endsWith('collectohub.com') &&
            url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') return null;
        return url.pathname;
    } catch {
        return null;
    }
};

export const getOptimizedImageUrl = (
    source: string | undefined,
    width: number,
    quality = 72
): string | undefined => {
    const imagePath = getOptimizableImagePath(source);
    if (!imagePath) return source;
    const params = new URLSearchParams({
        path: imagePath,
        width: String(width),
        quality: String(quality),
    });
    return `${APP_CONFIG.API_BASE}/media/image?${params.toString()}`;
};

export const getResponsiveImageProps = (
    source: string | undefined,
    widths: number[],
    quality = 72
): { src: string | undefined; srcSet?: string } => {
    const normalizedWidths = [...new Set(widths)]
        .filter((width) => Number.isFinite(width) && width > 0)
        .sort((left, right) => left - right);
    if (!source || normalizedWidths.length === 0 || !getOptimizableImagePath(source)) {
        return { src: source };
    }

    return {
        src: getOptimizedImageUrl(source, normalizedWidths[normalizedWidths.length - 1], quality),
        srcSet: normalizedWidths
            .map((width) => `${getOptimizedImageUrl(source, width, quality)} ${width}w`)
            .join(', '),
    };
};

/**
 * Compresses an image file on the client side using HTML5 Canvas.
 * Resizes the image so that its width and height do not exceed maxDimension.
 * Encodes the image to image/jpeg with a given quality.
 */
export const compressImage = (file: File, maxDimension = 800, quality = 0.7): Promise<File> => {
    return new Promise((resolve) => {
        if (!file || !file.type.startsWith("image/") || file.type.includes("gif") || file.type.includes("svg")) {
            // Do not compress non-images, GIFs, or SVGs
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    resolve(file);
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // PNG ise PNG olarak, değilse JPEG olarak kaydet (PNG saydamlığı korur)
                const isPng = file.type === "image/png";
                const outputType = isPng ? "image/png" : "image/jpeg";
                const extension = isPng ? ".png" : ".jpg";

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const newFileName = file.name.substring(0, file.name.lastIndexOf(".")) + extension;
                            const compressedFile = new File([blob], newFileName, {
                                type: outputType,
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            resolve(file);
                        }
                    },
                    outputType,
                    isPng ? undefined : quality
                );
            };
            img.onerror = () => resolve(file);
            img.src = event.target?.result as string;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
    });
};

type CropAndCompressImageOptions = {
    quality?: number;
    targetHeight: number;
    targetWidth: number;
};

/**
 * Crops an image from the center to the target aspect ratio, then resizes it
 * to the requested dimensions for consistent banner/background rendering.
 */
export const cropAndCompressImage = (
    file: File,
    { targetWidth, targetHeight, quality = 0.82 }: CropAndCompressImageOptions
): Promise<File> => {
    return new Promise((resolve) => {
        if (!file || !file.type.startsWith("image/") || file.type.includes("gif") || file.type.includes("svg")) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const sourceAspect = img.width / img.height;
                const targetAspect = targetWidth / targetHeight;

                let cropWidth = img.width;
                let cropHeight = img.height;
                let cropX = 0;
                let cropY = 0;

                if (sourceAspect > targetAspect) {
                    cropWidth = Math.round(img.height * targetAspect);
                    cropX = Math.round((img.width - cropWidth) / 2);
                } else if (sourceAspect < targetAspect) {
                    cropHeight = Math.round(img.width / targetAspect);
                    cropY = Math.round((img.height - cropHeight) / 2);
                }

                const canvas = document.createElement("canvas");
                canvas.width = targetWidth;
                canvas.height = targetHeight;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    resolve(file);
                    return;
                }

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(
                    img,
                    cropX,
                    cropY,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    targetWidth,
                    targetHeight
                );

                const isPng = file.type === "image/png";
                const outputType = isPng ? "image/png" : "image/jpeg";
                const extension = isPng ? ".png" : ".jpg";

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const baseName = file.name.includes(".")
                                ? file.name.substring(0, file.name.lastIndexOf("."))
                                : file.name;
                            const optimizedFile = new File([blob], `${baseName}${extension}`, {
                                type: outputType,
                                lastModified: Date.now()
                            });
                            resolve(optimizedFile);
                        } else {
                            resolve(file);
                        }
                    },
                    outputType,
                    isPng ? undefined : quality
                );
            };
            img.onerror = () => resolve(file);
            img.src = event.target?.result as string;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
    });
};
